import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { GitPullRequest, Sliders, Play, RotateCcw, Save } from 'lucide-react';
// Mock Data
const generateScenarioData = (factor: number) => {
  return Array.from({
    length: 24
  }, (_, i) => ({
    time: `${i}:00`,
    base: 50 + Math.sin(i / 3) * 20,
    scenario: 50 + Math.sin(i / 3) * 20 * factor + (factor > 1 ? 10 : -10)
  }));
};
const CustomTooltip = ({
  active,
  payload,
  label
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-white/95 backdrop-blur-xl border border-slate-200 rounded-xl p-3 shadow-xl z-50" data-id="element-3032">
        <p className="text-xs font-bold text-slate-900 mb-1" data-id="element-3033">{label}</p>
        {payload.map((entry: any) => <div key={entry.name} className="flex items-center gap-2 text-xs" data-id="element-3034">
            <div className="w-2 h-2 rounded-full" style={{
          backgroundColor: entry.color
        }} data-id="element-3035" />
            <span className="text-slate-600 capitalize" data-id="element-3036">{entry.name}:</span>
            <span className="font-mono font-medium" data-id="element-3037">
              €{entry.value.toFixed(2)}
            </span>
          </div>)}
      </div>;
  }
  return null;
};
export function ScenarioPlanningDashboard() {
  const [windFactor, setWindFactor] = useState(1.2);
  const [data, setData] = useState(generateScenarioData(1.2));
  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    setWindFactor(val);
    setData(generateScenarioData(val));
  };
  return <div className="min-h-screen p-6 space-y-6 pb-20 bg-slate-50" data-id="element-3038">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-3039">
        <div data-id="element-3040">
          <div className="flex items-center gap-2 mb-1" data-id="element-3041">
            <div className="p-2 bg-purple-600 rounded-lg" data-id="element-3042">
              <GitPullRequest size={20} className="text-white" data-id="element-3043" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" data-id="element-3044">
              Scenario Planning
            </h1>
          </div>
          <p className="text-slate-500 text-sm md:text-base ml-11" data-id="element-3045">
            Interactive what-if analysis for price forecasting
          </p>
        </div>

        <div className="flex gap-2" data-id="element-3046">
          <button className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50" data-id="element-3047">
            <RotateCcw size={16} data-id="element-3048" /> Reset
          </button>
          <button className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 shadow-sm" data-id="element-3049">
            <Save size={16} data-id="element-3050" /> Save Scenario
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-3051">
        {/* Controls */}
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} className="lg:col-span-1 bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm space-y-6" data-id="element-3052">
          <div className="flex items-center gap-2 mb-4" data-id="element-3053">
            <Sliders size={18} className="text-slate-700" data-id="element-3054" />
            <h3 className="font-bold text-slate-900" data-id="element-3055">Variables</h3>
          </div>

          <div data-id="element-3056">
            <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-3057">
              Wind Generation
            </label>
            <input type="range" min="0.5" max="1.5" step="0.1" value={windFactor} onChange={handleSliderChange} className="w-full accent-purple-600" data-id="element-3058" />
            <div className="flex justify-between text-xs text-slate-500 mt-1" data-id="element-3059">
              <span data-id="element-3060">-50%</span>
              <span className="font-bold text-purple-600" data-id="element-3061">
                {(windFactor * 100).toFixed(0)}%
              </span>
              <span data-id="element-3062">+50%</span>
            </div>
          </div>

          <div data-id="element-3063">
            <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-3064">
              Gas Price
            </label>
            <input type="range" className="w-full accent-purple-600" disabled data-id="element-3065" />
            <div className="flex justify-between text-xs text-slate-500 mt-1" data-id="element-3066">
              <span data-id="element-3067">€20</span>
              <span data-id="element-3068">€45</span>
              <span data-id="element-3069">€80</span>
            </div>
          </div>

          <div data-id="element-3070">
            <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-3071">
              Demand Growth
            </label>
            <input type="range" className="w-full accent-purple-600" disabled data-id="element-3072" />
            <div className="flex justify-between text-xs text-slate-500 mt-1" data-id="element-3073">
              <span data-id="element-3074">-5%</span>
              <span data-id="element-3075">0%</span>
              <span data-id="element-3076">+5%</span>
            </div>
          </div>
        </motion.div>

        {/* Main Chart */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="lg:col-span-3 bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-3077">
          <div className="flex justify-between items-center mb-6" data-id="element-3078">
            <h3 className="text-lg font-bold text-slate-900" data-id="element-3079">
              Price Impact Analysis
            </h3>
            <span className="text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded border border-purple-100" data-id="element-3080">
              Delta: +€{(data[12].scenario - data[12].base).toFixed(2)}
            </span>
          </div>

          <div className="h-[400px] w-full" data-id="element-3081">
            <ResponsiveContainer width="100%" height="100%" data-id="element-3082">
              <AreaChart data={data} margin={{
              top: 10,
              right: 10,
              left: 0,
              bottom: 0
            }} data-id="element-3083">
                <defs data-id="element-3084">
                  <linearGradient id="colorBase" x1="0" y1="0" x2="0" y2="1" data-id="element-3085">
                    <stop offset="5%" stopColor="#94a3b8" stopOpacity={0.3} data-id="element-3086" />
                    <stop offset="95%" stopColor="#94a3b8" stopOpacity={0} data-id="element-3087" />
                  </linearGradient>
                  <linearGradient id="colorScenario" x1="0" y1="0" x2="0" y2="1" data-id="element-3088">
                    <stop offset="5%" stopColor="#9333ea" stopOpacity={0.3} data-id="element-3089" />
                    <stop offset="95%" stopColor="#9333ea" stopOpacity={0} data-id="element-3090" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" data-id="element-3091" />
                <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#64748b'
              }} interval={3} data-id="element-3092" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#64748b'
              }} unit="€" data-id="element-3093" />
                <Tooltip content={<CustomTooltip data-id="element-3095" />} data-id="element-3094" />
                <Legend data-id="element-3096" />
                <Area type="monotone" dataKey="base" name="Base Case" stroke="#64748b" fill="url(#colorBase)" strokeWidth={2} data-id="element-3097" />
                <Area type="monotone" dataKey="scenario" name="Scenario" stroke="#9333ea" fill="url(#colorScenario)" strokeWidth={2} data-id="element-3098" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>
    </div>;
}